<script lang="ts" setup>
import { toRef, computed, onMounted } from 'vue';
import {
  EnumerationEvaluationOptionDto,
  EnumerationEvaluationParameterDto,
  EnumerationTrackItDataTypeFilterDto,
} from '@/api/generator';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { relatedTo } from '@/components/icons/icons';
import { I18nKey } from '@/utils/typings';
import { mapValues } from 'lodash';
import { useField, useFieldArray, useSetFieldValue } from 'vee-validate';
import { createEmptyEnumerationTrackItDataTypeFilter } from './trackItDataTypeFilters';
import EvaluationsManualTasksEnumTileParamEvaluationStateTile from './EvaluationsManualTasksEnumTileParamEvaluationStateTile.vue';
import { mdiDragHorizontal } from '@mdi/js';
import { Sortable } from 'sortablejs-vue3';
import { SortableEvent, SortableOptions } from 'sortablejs';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import { useValidateFieldErrorMessage } from '@/components/forms/useValidateFieldErrorMessage';
import { translateEntityProperty } from '@/utils/helper';
import EvaluationCard from './EvaluationCard.vue';

interface EvaluationsManualTasksEnumTileProps {
  name: string; // evaluationscopes[0].evaluationparameters[{index}]
  // The Track-it filter card only applies to Track-it import tasks, not plain manual tasks.
  isTrackItImport?: boolean;
}

const props = defineProps<EvaluationsManualTasksEnumTileProps>();

const mode = useFormMode();

const emits = defineEmits<{
  remove: [param: EnumerationEvaluationParameterDto];
}>();

const { value: tileData } = useField<EnumerationEvaluationParameterDto>(toRef(() => props.name));

const { fields: stateArray, move } = useFieldArray<EnumerationEvaluationOptionDto>(
  toRef(() => `${props.name}.options`),
);

const { validate: validateOptions, errorMessage: optionsErrorMessage } =
  useValidateFieldErrorMessage(toRef(() => `${props.name}.options`));

function handleRemove() {
  emits('remove', tileData.value);
}

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    move(e.oldIndex, e.newIndex);
  }
}

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.handle',
  disabled: mode.value === 'details',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));

const showActions = computed(() => mode.value !== 'details');

const setTrackItFilter = useSetFieldValue<EnumerationTrackItDataTypeFilterDto>(
  toRef(() => `${props.name}.trackItFilter`),
);

// The fields below bind into the filter, so it has to exist before they write to it — parameters
// saved before Track-it filters existed come without one.
onMounted(() => {
  if (props.isTrackItImport && mode.value !== 'details' && !tileData.value.trackItFilter) {
    setTrackItFilter(createEmptyEnumerationTrackItDataTypeFilter(), false);
  }
});

export type TrackItFilterValue = 'True' | 'False' | 'Warning';

const trackItFilterOptionFields = {
  True: 'enumerationOptionTrue',
  False: 'enumerationOptionFalse',
  Warning: 'enumerationOptionWarning',
} as const satisfies Record<TrackItFilterValue, keyof EnumerationTrackItDataTypeFilterDto>;

const trackItFilterValues = Object.keys(trackItFilterOptionFields) as TrackItFilterValue[];

const setTrackItFilterOption = mapValues(trackItFilterOptionFields, (field) =>
  useSetFieldValue<number | undefined>(toRef(() => `${props.name}.trackItFilter.${field}`)),
);

function optionOf(value: TrackItFilterValue) {
  return tileData.value.trackItFilter?.[trackItFilterOptionFields[value]] ?? undefined;
}

function trackItFilterValueForOption(optionId: number | undefined): TrackItFilterValue | undefined {
  if (optionId === undefined) return undefined;
  return trackItFilterValues.find((value) => optionOf(value) === optionId);
}

// Each option can hold at most one of True/False/Warning — picking one for an option clears
// it from whichever of the other two slots it previously occupied.
function setTrackItFilterValueForOption(
  optionId: number | undefined,
  value: TrackItFilterValue | undefined,
) {
  if (optionId === undefined) return;
  for (const filterValue of trackItFilterValues) {
    if (filterValue === value) {
      setTrackItFilterOption[filterValue](optionId);
    } else if (optionOf(filterValue) === optionId) {
      setTrackItFilterOption[filterValue](undefined);
    }
  }
}

// Values already claimed by a different option — offered disabled in that option's own select
// so picking one can't silently steal it away from wherever it's currently assigned.
function takenTrackItFilterValues(excludingOptionId: number | undefined): TrackItFilterValue[] {
  return trackItFilterValues.filter((value) => {
    const optionId = optionOf(value);
    return optionId != null && optionId !== excludingOptionId;
  });
}
</script>

<template>
  <EvaluationCard data-testid="EvaluationEnumTile">
    <template #leading>
      <v-icon
        v-if="showActions"
        :icon="mdiDragHorizontal"
        class="handle cursor-move"
        data-testid="enum-drag"
      />
    </template>

    <template #title>
      {{ translateEntityProperty('name', tileData) }}
    </template>

    <template v-if="showActions" #actions>
      <PtwDeleteButton data-testid="enum-delete" @delete="handleRemove" />
    </template>

    <div class="enum-column">
      <p class="section-heading">{{ $t('Enumeration_one') }}</p>
      <EnumOrQuantityLabel :data="tileData" :label="$t('Enumeration_one')" />
      <div data-testid="enum-instruction">
        <p class="section-heading">{{ $t('InstructionForTaskExecution' as I18nKey) }}</p>
        <VeeTextarea :label="$t('Instruction_one')" :name="`${name}.instruction`" :mode="mode" />
      </div>
    </div>

    <div data-testid="enum-results">
      <p class="section-heading">{{ $t('Enumeration_one') }}</p>
      <p class="section-hint">{{ $t('EnumerationEvaluationHint' as I18nKey) }}</p>

      <div class="enum-list-header" :class="{ 'is-details': mode === 'details' }">
        <span>{{ $t('EnumerationItem_other') }}</span>
        <span class="enum-list-header__result">{{ $t('EvaluationResult') }}</span>
      </div>

      <InlineNotification
        v-if="optionsErrorMessage"
        class="mb-2"
        :value="{ type: 'error', message: optionsErrorMessage }"
      />

      <div v-if="isTrackItImport" class="d-flex align-center ga-2 mb-1 option-header">
        <v-icon
          v-if="mode !== 'details'"
          :icon="mdiDragHorizontal"
          class="mr-2"
          style="visibility: hidden"
        />
        <span class="option-header-name-spacer" />
        <p class="v-label v-label-overwrite option-header-filter-value">
          {{ $t('TrackItFilterValue' as I18nKey) }}
        </p>
        <span class="option-header-state-spacer" />
      </div>

      <Sortable
        class="w-100"
        :list="stateArray"
        :options="sortableOptions"
        itemKey="key"
        @end="sortingEndHandler"
      >
        <template #item="{ index, key }">
          <EvaluationsManualTasksEnumTileParamEvaluationStateTile
            :key="key"
            :name="`${props.name}.options[${index}]`"
            :isTrackItImport="isTrackItImport"
            :trackItFilterValue="trackItFilterValueForOption(stateArray[index]?.value.id)"
            :disabledTrackItFilterValues="takenTrackItFilterValues(stateArray[index]?.value.id)"
            @enabled="validateOptions()"
            @update:trackItFilterValue="
              (value) => setTrackItFilterValueForOption(stateArray[index]?.value.id, value)
            "
          />
        </template>
      </Sortable>

      <v-card v-if="isTrackItImport" class="container-level-3 track-it-filter-box mt-3">
        <v-card-item>
          <v-card-title class="d-flex align-center ga-1">
            <v-icon :icon="relatedTo" size="16" />
            <h6>{{ $t('TrackItFilter' as I18nKey) }}</h6>
          </v-card-title>
        </v-card-item>
        <v-card-text>
          <VeeTextField
            :name="`${name}.trackItFilter.dataType.name`"
            :label="$t('Name')"
            density="compact"
          />
          <VeeTextField
            :name="`${name}.trackItFilter.dataType.definition`"
            :label="$t('Definition_one')"
            density="compact"
          />
        </v-card-text>
      </v-card>
    </div>
  </EvaluationCard>
</template>

<style lang="scss" scoped>
.track-it-filter-box {
  max-width: 20em;

  // The global VTextField default (bgColor: 'surface', #FAFAFA) reads as grey against
  // this box's own near-white background - white it explicitly for contrast.
  :deep(.v-field) {
    background-color: white;
  }
}

.enum-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  margin: 0;
  color: rgb(var(--v-theme-primary-darken-2));
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.section-hint {
  margin: 8px 0 16px;
  color: rgb(var(--v-theme-primary));
  font-size: 16px;
  line-height: 20px;
}

.enum-list-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding-left: 40px;
  color: rgb(var(--v-theme-primary-lighten-1));
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}

.enum-list-header.is-details {
  padding-left: 8px;
}

.enum-list-header__result {
  width: 286px;
  margin-left: auto;
  flex: 0 0 286px;
}

// Matches EvaluationsManualTasksEnumTileParamEvaluationStateTile.vue's own row layout (incl.
// its .container's padding-left) so this heading lines up above that row's two select boxes —
// the name-spacer stands in for the checkbox, the trailing spacer for the evaluation-state
// select, so the filter-value label ends up over the filter-value select in between.
.option-header {
  padding-left: 8px;
}
.option-header-name-spacer {
  flex: 1 1 auto;
}
.option-header-filter-value {
  width: 12em;
  flex: 0 0 auto;
}
.option-header-state-spacer {
  width: 286px;
  flex: 0 0 auto;
}
</style>
